/**
 * Member Settings Page for Re:Nav-96
 *
 * Allows members to configure their account settings and preferences.
 * Includes profile settings, notification preferences, and digest subscriptions.
 *
 * Features:
 * - Profile information display
 * - Email digest subscription settings
 * - Notification preferences (placeholder for future)
 * - Account actions
 *
 * @example
 * // In router configuration
 * { path: 'settings', element: <SettingsPage /> }
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { DigestSettings } from '@/components/member/DigestSettings';
import './SettingsPage.css';

/**
 * Settings tab type
 */
type SettingsTab = 'profile' | 'notifications' | 'digest';

/**
 * Tab configuration
 */
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: React.ReactNode;
}

/**
 * Available settings tabs
 */
const tabs: TabConfig[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
  },
  {
    id: 'digest',
    label: 'Email Digest',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-5 5v-5zm-5 5h2v-2h-2v2zm8-10a6 6 0 11-12 0 6 6 0 0112 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
    ),
  },
];

/**
 * Profile section component
 */
function ProfileSection() {
  const { user } = useAuth();

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h3 className="settings-section-title">Profile Information</h3>
        <p className="settings-section-description">
          Manage your personal information and account details.
        </p>
      </div>

      <div className="profile-info-card">
        <div className="profile-avatar">
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName}
              className="profile-avatar-image"
            />
          ) : (
            <span className="profile-avatar-placeholder">
              {user?.displayName?.charAt(0).toUpperCase() || 'U'}
            </span>
          )}
        </div>
        <div className="profile-details">
          <div className="profile-field">
            <span className="profile-field-label">Display Name</span>
            <span className="profile-field-value">{user?.displayName}</span>
          </div>
          <div className="profile-field">
            <span className="profile-field-label">Email</span>
            <span className="profile-field-value">
              {user?.email || 'Not set'}
              {user?.emailVerified && (
                <span className="profile-verified-badge" title="Verified">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              )}
            </span>
          </div>
          <div className="profile-field">
            <span className="profile-field-label">Phone</span>
            <span className="profile-field-value">
              {user?.phone || 'Not set'}
              {user?.phoneVerified && (
                <span className="profile-verified-badge" title="Verified">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              )}
            </span>
          </div>
          <div className="profile-field">
            <span className="profile-field-label">Member Since</span>
            <span className="profile-field-value">
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '--'}
            </span>
          </div>
          <div className="profile-field">
            <span className="profile-field-label">Role</span>
            <span className="profile-field-value profile-role">
              {user?.role === 'super_admin'
                ? 'Super Admin'
                : user?.role === 'group_admin'
                ? 'Group Admin'
                : 'Member'}
            </span>
          </div>
        </div>
      </div>

      <div className="profile-actions">
        <button className="btn-secondary" disabled>
          Edit Profile
          <span className="coming-soon-badge">Coming Soon</span>
        </button>
        <button className="btn-secondary" disabled>
          Change Password
          <span className="coming-soon-badge">Coming Soon</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Notifications section component (placeholder)
 */
function NotificationsSection() {
  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h3 className="settings-section-title">Notification Preferences</h3>
        <p className="settings-section-description">
          Control how and when you receive notifications.
        </p>
      </div>

      <div className="settings-placeholder">
        <svg
          className="w-16 h-16 text-white/20 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        <h4 className="text-white/80 font-medium mb-2">Coming Soon</h4>
        <p className="text-white/50 text-sm text-center max-w-md">
          Notification preferences will allow you to control SMS, WhatsApp, and email
          notifications for various events and activities.
        </p>
      </div>
    </div>
  );
}

/**
 * Digest section component
 */
function DigestSection() {
  return (
    <div className="settings-section">
      <DigestSettings />
    </div>
  );
}

/**
 * SettingsPage Component
 *
 * Main settings page for member account management.
 * Uses a tabbed interface to organize different setting categories.
 *
 * @returns The member settings page content
 *
 * @example
 * // Used in router
 * { path: 'settings', element: <SettingsPage /> }
 */
export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('digest');

  return (
    <div className="settings-page">
      {/* Page header — primary title is rendered by MemberHeader. We keep
          only the back link here to save vertical space. */}
      <div className="settings-header">
        <div className="settings-header-content">
          <Link to="../dashboard" relative="path" className="settings-back-link">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="settings-tabs">
        <div className="settings-tabs-inner">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="settings-content">
        {activeTab === 'profile' && <ProfileSection />}
        {activeTab === 'digest' && <DigestSection />}
        {activeTab === 'notifications' && <NotificationsSection />}
      </div>
    </div>
  );
}

export default SettingsPage;
